/* eslint-env browser */
import debounce from "lodash/debounce";
import { Controller } from "stimulus";
import { constrain } from "../../helpers/numbers";

export default class extends Controller {
  static targets = ["actionsBar", "selectedActions", "filteringActions", "flaggingDropdown", "selectedItemCount"];

  static values = {
    flagging: { type: Boolean, default: false },
    flaggingType: String,
    selectedOneLabel: String,
    selectedManyLabel: String,
  };

  syncDependentFields = debounce(this.#syncDependentFields.bind(this), 10);

  controllerStopped = false;

  connect() {
    this.connectControllers();
  }

  disconnect() {
    this.controllerStopped = true;
  }

  cancelSelections() {
    this.dispatch("receivingReset", { prefix: false });

    this.resetFlaggingMode();
    this.actionsBarTarget.dataset.display = "filters";
  }

  configureToolbar() {
    const receivableGoods = this.#receivableGoods(this.element).filter((receivable) =>
      this.#goodIsMainControl(receivable),
    );

    const totalAllocated = receivableGoods.reduce(
      (value, receivable) =>
        value + Math.abs(this.#goodAllocation(receivable) - this.#goodInitialAllocation(receivable)),
      0,
    );

    const selected = totalAllocated === 1 ? this.selectedOneLabelValue : this.selectedManyLabelValue;

    this.selectedItemCountTarget.textContent = `${totalAllocated} ${selected}`;
    this.actionsBarTarget.dataset.display = totalAllocated || this.flaggingModeValue ? "selected" : "filters";
  }

  flaggingModeSelected(event) {
    const { value } = event.detail;

    this.flaggingModeValue = value !== "";
    this.flaggingTypeValue = value;

    this.flaggingDropdownTargets.forEach((element) => {
      element.querySelector("[value=reset]")?.classList.toggle("hidden", !this.flaggingModeValue);
      element.querySelector("[role=afterButtonIcon]")?.classList.toggle("hidden", this.flaggingModeValue);

      this.application
        .getControllerForElementAndIdentifier(element, "ui--dropdown--dropdown")
        .updateSelectedValue(event);
    });

    this.configureToolbar();
  }

  resetFlaggingMode() {
    this.flaggingModeValue = false;
    this.flaggingTypeValue = "";

    this.flaggingDropdownTargets.forEach((element) => {
      const controller = this.application.getControllerForElementAndIdentifier(element, "ui--dropdown--dropdown");

      controller.selectionChangedValue = this.flaggingModeValue;
      controller.selectedValue = this.flaggingTypeValue;

      element.querySelector("[value=reset]")?.classList.add("hidden");
      element.querySelector("[role=afterButtonIcon]")?.classList.remove("hidden");

      controller.updateSelectedValue({
        detail: {
          value: this.flaggingTypeValue,
          selectionChanged: this.flaggingModeValue,
        },
      });
    });

    this.configureToolbar();
  }

  submit() {
    this.dispatch("receivingRefresh", { prefix: false });

    this.actionsBarTarget.dataset.display = "filters";
  }

  #syncDependentFields(event) {
    const targetReceivable = event.detail.instance.element;
    const container = targetReceivable.closest("[data-component=groupRow]");
    const receivableGoods = this.#receivableGoods(container).filter(
      (receivable) => receivable.dataset.size === targetReceivable.dataset.size,
    );

    if (this.#goodIsMainControl(targetReceivable)) {
      // The main control for a good size was adjusted, so adjust any of its grouped controls.
      const leftover = receivableGoods
        .filter((receivable) => !this.#goodIsMainControl(receivable))
        .reduce((value, receivable) => {
          const newValue = constrain(value, 0, this.#goodMaxAllocation(receivable));
          this.#updateGoodAllocation(receivable, newValue, true);

          return value - newValue;
        }, this.#goodAllocation(targetReceivable));

      this.#updateGoodAllocation(
        receivableGoods[1],
        this.#goodAllocation(receivableGoods[1]) + Math.max(0, leftover),
        true,
      );
    } else {
      // A grouped control for a good size was adjusted, so adjust the main control as well.
      const newValue = receivableGoods
        .filter((receivable) => !this.#goodIsMainControl(receivable))
        .reduce((value, receivable) => value + this.#goodAllocation(receivable), 0);
      this.#updateGoodAllocation(receivableGoods[0], newValue, false);
    }

    this.#syncRowCheckboxWithReceivableCheckboxes(container);
    this.#syncCardCheckboxWithRowCheckboxes(container.closest("[role=group]"));
    this.dispatch("syncStatusIndicator", { prefix: false });
    this.configureToolbar();
  }

  #updateGoodAllocation(goodElement, newValue, mainUpdated) {
    goodElement.dispatchEvent(new CustomEvent("receivingAllocation", { detail: { value: newValue, mainUpdated } }));
  }

  #receivableGoods(container) {
    return Array.from(container.querySelectorAll("[data-component=receivableGood]"));
  }

  #syncRowCheckboxWithReceivableCheckboxes(row) {
    const rowCheckbox = row.querySelector("[data-component=groupRowCheckbox]");
    const receivableGoods = this.#receivableGoods(row)
      .filter((receivable) => this.#goodIsMainControl(receivable))
      .filter((receivable) => this.#goodNeedsReceived(receivable));

    rowCheckbox.checked = receivableGoods.some((receivable) => this.#goodIsDirty(receivable));
    rowCheckbox.indeterminate =
      rowCheckbox.checked &&
      receivableGoods.some((receivable) => {
        const goodsCheckbox = receivable.querySelector("[data-component='goodsCheckbox']");
        return !goodsCheckbox.checked || goodsCheckbox.indeterminate;
      });
  }

  #syncCardCheckboxWithRowCheckboxes(card) {
    const cardCheckbox = card.querySelector("[data-component='cardCheckbox']");
    const rowCheckboxes = [...card.querySelectorAll("[data-component='groupRowCheckbox']")];

    cardCheckbox.checked = rowCheckboxes.some((checkbox) => checkbox.checked);
    cardCheckbox.indeterminate =
      cardCheckbox.checked && rowCheckboxes.some((checkbox) => checkbox.checked && checkbox.indeterminate);
  }

  #goodAllocation(good) {
    return parseInt(good.dataset["goods-ReceivableGoodAllocationValue"], 10);
  }

  #goodMaxAllocation(good) {
    return parseInt(good.dataset["goods-ReceivableGoodMaxAllocationValue"], 10);
  }

  #goodInitialAllocation(good) {
    return parseInt(good.dataset["goods-ReceivableGoodInitialAllocationValue"], 10);
  }

  #goodIsDirty(good) {
    return good.dataset["goods-ReceivableGoodDirtyValue"] === "true";
  }

  #goodNeedsReceived(good) {
    return good.dataset.status !== "received";
  }

  #goodIsMainControl(good) {
    return good.dataset.control === "main";
  }

  connectControllers() {
    const main = Array.from(this.element.querySelectorAll("[data-control=main][data-controller-delay]"));
    const hidden = Array.from(this.element.querySelectorAll(":not([data-control=main])[data-controller-delay]"));

    const delay = [...main, ...hidden];
    if (delay.length > 0) {
      const loader = (callNext = true) => {
        for (let i = 0; i < 5; i += 1) {
          const element = delay.shift();
          if (!element) return;

          const { controllerDelay, controller } = element.dataset;
          const delayedControllers = controllerDelay.split(" ");
          const dataControllers = controller || "";
          const controllerToAdd = delayedControllers.filter(
            (delayedController) => delayedController && !dataControllers.includes(delayedController),
          );

          if (controllerToAdd.length > 0) {
            element.dataset.controller = `${dataControllers} ${controllerToAdd[0]}`.trim();
            if (controllerToAdd.length > 1) {
              delay.push(element);
            }
          }
        }

        if (callNext && !this.controllerStopped) {
          setTimeout(loader);
        }
      };

      // prevent too many visual updates on the initial show
      for (let i = 0; i < 5; i += 1) {
        loader(false);
      }
      loader();
    }
  }
}
